import { Link } from 'react-router';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Related = {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  tags: string[];
};

type Props = { items: ReadonlyArray<Related> };

const GRADIENTS: ReadonlyArray<string> = [
  'bg-[conic-gradient(from_45deg,#84CC16_0deg,#1A1A2E_120deg,#F5F3EF_240deg,#84CC16_360deg)]',
  'bg-[linear-gradient(135deg,#1A1A2E_0%,#84CC16_100%)]',
  'bg-[conic-gradient(from_90deg,#EA580C_0deg,#1A1A2E_180deg,#EA580C_360deg)]',
];

function formatDate(d: string) {
  try {
    return new Date(d)
      .toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
      .toUpperCase();
  } catch {
    return d;
  }
}

export default function BlogPostRelatedV2({ items }: Props) {
  if (!items.length) return null;

  return (
    <section className="bg-white py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>Lire ensuite</SectionLabel>
        <h2 className="mt-6 font-marketing-display text-[clamp(28px,3.6vw,44px)] font-bold leading-[1.05] tracking-[-0.03em] text-charcoal-800">
          Des articles qui <span className="underline-lime">complètent celui-ci</span>.
        </h2>

        <ul className="mt-12 grid gap-7 md:grid-cols-2 xl:grid-cols-3">
          {items.map((a, idx) => {
            const gradient = GRADIENTS[idx % GRADIENTS.length];
            const category = a.tags[0] ?? 'Article';
            return (
              <li key={a.slug}>
                <Link
                  to={`/blog/${a.slug}`}
                  className="group flex h-full flex-col border-2 border-charcoal-800 bg-white shadow-neobrutal-6 transition-transform hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-10"
                >
                  <div className={`relative h-40 ${gradient}`}>
                    <span className="absolute bottom-4 left-4 border-2 border-charcoal-800 bg-white px-2.5 py-1 font-marketing-mono text-[10px] font-bold uppercase tracking-[0.12em] text-charcoal-800 shadow-neobrutal-2">
                      {category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col border-t-2 border-charcoal-800 p-6">
                    <div className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                      {formatDate(a.date)} · Par {a.author}
                    </div>
                    <h3 className="mt-3 font-marketing-display text-[19px] font-bold leading-[1.2] tracking-[-0.02em] text-charcoal-800">
                      {a.title}
                    </h3>
                    <p className="mt-3 line-clamp-2 font-marketing-body text-[14.5px] leading-relaxed text-charcoal-600">
                      {a.description}
                    </p>
                    <div className="mt-auto pt-5">
                      <span className="inline-flex items-center gap-2 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.14em] text-lime-700 group-hover:text-lime-900">
                        Lire l'article
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.6"
                        >
                          <path d="M5 12h14" />
                          <path d="M13 6l6 6-6 6" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
